import React, { useCallback, useEffect, useState } from "react";
import { routes } from "@/router";
import useSearchStore from "@/stores/useSearchStore";
import { buildCompanySelection, buildSearchRequestBody } from "@/utils/searchRequestBuilder.util";
import { CompanyEntryDto } from "@advanced-sales-technologies/backend-client/models/CompanyEntryDto";
import { ListConfigurationDto } from "@advanced-sales-technologies/backend-client/models/ListConfigurationDto";
import { SelectOptionRequestDto } from "@advanced-sales-technologies/backend-client/models/SelectOptionRequestDto";
import { TabGroup, TabList, TabPanel, TabPanels } from "@headlessui/react";
import { UseQueryResult } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { HiArrowRight, HiOutlineUser, HiOutlineUserGroup, HiUser, HiUserGroup } from "react-icons/hi2";
import { useNavigate } from "react-router-dom";

import { useGetExternalPersonEmails } from "@/hooks/api/list/options/useGetExternalPersonEmails";
import { useGetPersonEmails } from "@/hooks/api/list/options/useGetPersonEmails";
import { useUpdateSelectOption } from "@/hooks/api/list/useUpdateSelectOption";
import { SearchParams } from "@/hooks/api/search/useInfiniteSearch";
import useTierLimit from "@/hooks/useTierLimit";
import Button from "@/components/atoms/Button";
import MyTab from "@/components/atoms/tab/MyTab";
import FeaturedText from "@/components/molecules/FeaturedText";
import SearchInput from "@/components/molecules/SearchInput";
import HasMoreDataCellBase, { HasMoreDataCellBaseProps } from "@/components/molecules/table/HasMoreDataCellBase";
import { ContactPersonColumnProps } from "@/components/molecules/table/search/columns/PersonRoleColumn";
import SelectAsyncComboBox from "@/components/organisms/SelectAsyncComboBox";

type HasMoreDataCellProps<OptionTypeGeneric extends { id: string }> = {
  initialSelected?: string | undefined;
  queryKey: readonly unknown[];
  company: CompanyEntryDto;
  searchParams?: SearchParams;
  listId?: string | undefined | null;
  placeholder: string;
  useQueryHook: (
    query: string | null,
    enabled: boolean,
    convertedSearchParams: ListConfigurationDto,
  ) => UseQueryResult<OptionTypeGeneric[], Error>;
  useExternalQueryHook: (
    query: string | null,
    enabled: boolean,
    convertedSearchParams: ListConfigurationDto,
  ) => UseQueryResult<OptionTypeGeneric[], Error>;
  renderOption: (option: OptionTypeGeneric, isSelected: boolean) => React.ReactNode;
  buildDisplayValue: (option: OptionTypeGeneric) => string;
} & Omit<HasMoreDataCellBaseProps, "isPending" | "onOpenChange" | "hasMoreData" | "children"> &
  ContactPersonColumnProps;

const HasMoreDataCellPersonRole = <OptionTypeGeneric extends { id: string }>({
  initialSelected,
  displayValue,
  queryKey,
  company,
  searchParams,
  listId,
  placeholder,
  useQueryHook,
  useExternalQueryHook,
  renderOption,
  buildDisplayValue,
  renderValue = (value) => `${value}`,
  onShowPersonColumns,
}: HasMoreDataCellProps<OptionTypeGeneric>) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isTyping, setIsTyping] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const { isFreeTier } = useTierLimit();

  const [selectedId, setSelectedId] = useState<string | undefined>(initialSelected);
  const [convertedSearchParams, setConvertedSearchParams] = useState<ListConfigurationDto>({});
  const [dynamicDisplayValue, setDynamicDisplayValue] = useState<string | undefined | null>(displayValue);

  const { getExternalEmails } = useGetExternalPersonEmails(company.id);
  const { getEmails } = useGetPersonEmails(company.id);

  const { updateSelectOption, isPending: updateIsLoading } = useUpdateSelectOption();
  const addChangedCompanyIds = useSearchStore((state) => state.addChangedCompanyIds);

  useEffect(() => {
    if (initialSelected) {
      setSelectedId(initialSelected);
    }
  }, [initialSelected]);

  useEffect(() => {
    setDynamicDisplayValue(displayValue);
  }, [displayValue]);

  const [query, setQuery] = useState<string>("");
  const { data, isLoading } = useQueryHook(
    query.trim() === "" ? null : query,
    isOpen && selectedTab === 0,
    convertedSearchParams,
  );

  const { data: externalData, isLoading: isLoadingExternal } = useExternalQueryHook(
    query.trim() === "" ? null : query,
    isOpen && selectedTab === 1 && !isFreeTier,
    convertedSearchParams,
  );

  const updateOption = useCallback(
    (optionId: string, target: SelectOptionRequestDto.target) => {
      updateSelectOption(
        {
          companyId: company.id,
          option: {
            list_id: listId,
            target: target,
            target_id: optionId,
            configuration: convertedSearchParams,
            selections: buildCompanySelection(company),
          },
          queryKey: queryKey,
        },
        {
          onSuccess: (newCompany) => {
            addChangedCompanyIds(company.id);

            const fetchEmailsHook =
              target === SelectOptionRequestDto.target.EXTERNAL_CONTACT_PERSON ? getExternalEmails : getEmails;

            fetchEmailsHook({
              id: optionId,
              companyId: newCompany.id,
              option: {
                list_id: listId,
                configuration: convertedSearchParams,
                selections: buildCompanySelection(newCompany),
              },
              queryKey,
            });
          },
        },
      );
    },
    [
      company.id,
      convertedSearchParams,
      listId,
      queryKey,
      updateSelectOption,
      getExternalEmails,
      getEmails,
      addChangedCompanyIds,
    ],
  );

  const onSelected = useCallback(
    (option: OptionTypeGeneric, target: SelectOptionRequestDto.target) => {
      setSelectedId(option.id);
      setDynamicDisplayValue(buildDisplayValue(option));
      updateOption(option.id, target);
    },
    [updateOption, buildDisplayValue],
  );

  useEffect(() => {
    if (searchParams) {
      setConvertedSearchParams(buildSearchRequestBody(searchParams));
    }
  }, [searchParams]);

  return (
    <HasMoreDataCellBase
      renderValue={renderValue}
      hasMoreData={true}
      displayValue={dynamicDisplayValue}
      isPending={updateIsLoading}
      onOpenChange={setIsOpen}
    >
      <SearchInput
        className={"px-4"}
        onQueryUpdate={setQuery}
        onTyping={setIsTyping}
        placeholder={t("search.searchFor", { column: placeholder })}
      />

      <TabGroup selectedIndex={selectedTab} onChange={setSelectedTab}>
        <TabList className={"flex pt-2"}>
          <MyTab label={t("search.table.contact.tabs.officials")} activeIcon={HiUser} icon={HiOutlineUser} />
          <MyTab label={t("search.table.contact.tabs.all")} activeIcon={HiUserGroup} icon={HiOutlineUserGroup} />
        </TabList>

        <TabPanels>
          <TabPanel>
            <SelectAsyncComboBox
              canSearch={false}
              options={data}
              optionsClassName={"flex flex-col gap-1 max-h-72 w-[450px]"}
              renderOption={renderOption}
              isLoading={isLoading || isTyping}
              selected={selectedId}
              onSelect={(option) => onSelected(option, SelectOptionRequestDto.target.CONTACT_PERSON)}
            />
          </TabPanel>
          <TabPanel>
            {isFreeTier ? (
              <UpgradeToSeeMore />
            ) : (
              <SelectAsyncComboBox
                canSearch={false}
                options={externalData}
                optionsClassName={"flex flex-col gap-1 max-h-72 w-[450px]"}
                renderOption={renderOption}
                isLoading={isLoadingExternal || isTyping}
                selected={selectedId}
                onSelect={(selected) => {
                  onSelected(selected, SelectOptionRequestDto.target.EXTERNAL_CONTACT_PERSON);
                  // If external is selected we show additional columns
                  onShowPersonColumns?.(true);
                }}
              />
            )}
          </TabPanel>
        </TabPanels>
      </TabGroup>
    </HasMoreDataCellBase>
  );
};

export default React.memo(HasMoreDataCellPersonRole) as typeof HasMoreDataCellPersonRole;

const UpgradeToSeeMore = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <FeaturedText
      heading={t("search.table.contact.upgrade.title")}
      body={t("search.table.contact.upgrade.text")}
      className={"h-72 w-[450px] px-8 py-6"}
    >
      <div className={"mt-2"}>
        <Button
          label={t("settings.plans.options.upgrade")}
          onClick={() => navigate(routes.plans)}
          size={"xs"}
          trailingIcon={<HiArrowRight className={"size-4"} />}
        />
      </div>
    </FeaturedText>
  );
};
